import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { CambioOrientacionRepository } from 'src/app/domain/service/cambio-orientacion/cambio-orientacion-repository';
import {
  AnularRechazoOrdenRequest,
  CambioOrientacion,
  CambioOrientacionConfiguracion,
  CambioOrientacionFiltro,
  CambioOrientacionPage,
  OperacionOrdenesRequest,
  ResultadoAnularRechazo,
  ResultadoOperacion
} from 'src/app/domain/model/cambio-orientacion/cambio-orientacion';
import { validarSize } from 'src/app/common/validation-utils';

/**
 * Adaptador HTTP: HttpCambioOrientacionRepository
 * 
 * Implementa el puerto CambioOrientacionRepository para comunicación HTTP con backend.
 * Realiza llamadas REST al backend Spring Boot (CambioOrientacionController).
 * 
 * <p>Autenticación: El DNI del usuario se obtiene de la sesión SSO autenticada en el backend
 * vía {@code SesionUsuarioResolver}. El frontend NO envía el header X-User-DNI manualmente.</p>
 * 
 * Endpoints:
 * - GET  /api/servicios/cambio-orientacion/{nmconsecutivo}/detalle
 * - POST /api/servicios/cambio-orientacion/buscar
 * - POST /api/servicios/cambio-orientacion/rechazar
 * - POST /api/servicios/cambio-orientacion/bajar-saldo
 * 
 * Backend: 580-girop_servicios-ms (puerto 8080)
 * 
 * @author Sura - Migración GWT a Spring Boot
 */
@Injectable({ providedIn: 'root' })
export class HttpCambioOrientacionRepository implements CambioOrientacionRepository {

  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.backend.baseUrl}/servicios/cambio-orientacion`;

  async obtenerConfiguracion(): Promise<CambioOrientacionConfiguracion> {
    return await firstValueFrom(
      this.http.get<CambioOrientacionConfiguracion>(
        `${this.baseUrl}/configuracion`
      )
    );
  }

  /**
   * Obtiene el detalle completo de una orden por su consecutivo.
   * Usa query directo DETALLE_ORDEN con JOINs y funciones Oracle.
   */
  async obtenerDetalle(nmconsecutivo: number): Promise<CambioOrientacion> {
    return await firstValueFrom(
      this.http.get<CambioOrientacion>(
        `${this.baseUrl}/${nmconsecutivo}/detalle`
      )
    );
  }

  /**
   * Busca órdenes que pueden ser rechazadas o tener su saldo bajado, con
   * paginación keyset/cursor (skill acceso-datos-oracle §7 — nunca OFFSET).
   * cursor/direction viajan en el mismo cuerpo del POST junto con los
   * filtros (BuscarOrdenesRequest); el tamaño de página viaja en filtro.size.
   */
  async buscarOrdenes(filtro: CambioOrientacionFiltro): Promise<CambioOrientacionPage> {
    validarSize(filtro.size);
    const response = await firstValueFrom(
      this.http.post<CambioOrientacionPage>(`${this.baseUrl}/buscar`, filtro)
    );
    return (
      response ?? {
        content: [],
        size: filtro.size,
        nextCursor: null,
        previousCursor: null,
        hasNext: false,
        hasPrevious: false,
        totalElements: 0
      }
    );
  }

  /**
   * Rechaza una o más órdenes de servicio.
   */
  async rechazarOrdenes(request: OperacionOrdenesRequest): Promise<ResultadoOperacion[]> {
    const response = await firstValueFrom(
      this.http.post<ResultadoOperacion[]>(
        `${this.baseUrl}/rechazar`,
        request
      )
    );
    return response ?? [];
  }

  /**
   * Baja el saldo de una o más órdenes de servicio.
   */
  async bajarSaldoOrdenes(request: OperacionOrdenesRequest): Promise<ResultadoOperacion[]> {
    const response = await firstValueFrom(
      this.http.post<ResultadoOperacion[]>(
        `${this.baseUrl}/bajar-saldo`,
        request
      )
    );
    return response ?? [];
  }

  /**
   * Anula una solicitud de rechazo de orden.
   */
  async anularRechazoOrden(
    request: AnularRechazoOrdenRequest
  ): Promise<ResultadoAnularRechazo> {
    return await firstValueFrom(
      this.http.post<ResultadoAnularRechazo>(
        `${this.baseUrl}/anular-rechazo`,
        request
      )
    );
  }
}
